import { useEffect } from "react";

/**
 * <Sensitive level="highly_sensitive"> — mark a UI value as sensitive.
 *
 * Levels (per the Sensitive Data Classification spec):
 *   public           — never masked, never watermark-boosted
 *   internal         — never masked by default
 *   confidential     — masked when Hide Values is on OR in Print
 *                      (default for balances, transactions, etc.)
 *   highly_sensitive — masked ALWAYS in Print, and gets stronger blur
 *                      when Hide Values is on (card numbers, addresses)
 */
const ALLOWED = new Set(["public", "internal", "confidential", "highly_sensitive"]);

export function Sensitive({ level = "confidential", as: Tag = "span", children, className = "", ...rest }) {
    const l = ALLOWED.has(level) ? level : "confidential";
    return (
        <Tag
            className={`vault-sensitive vault-sensitive--${l} ${className}`}
            data-sensitivity={l}
            {...rest}
        >
            {children}
        </Tag>
    );
}

/**
 * usePageSensitivity — declare the max sensitivity of the current page
 * so the watermark opacity and shield behaviour can scale with it.
 * Call from a page component's top-level useEffect.
 */
export function usePageSensitivity(level = "confidential") {
    useEffect(() => {
        const l = ALLOWED.has(level) ? level : "confidential";
        document.body.setAttribute("data-page-sensitivity", l);
        return () => {
            if (document.body.getAttribute("data-page-sensitivity") === l) {
                document.body.removeAttribute("data-page-sensitivity");
            }
        };
    }, [level]);
}

/**
 * Convenience mask helper — turn "1234567812345678" into "•••• 5678"
 * and "salary@company.com" into "s••••@company.com". Use inside a
 * <Sensitive level="highly_sensitive"> wrapper.
 */
export function maskCard(num) {
    if (!num) return "";
    const s = String(num).replace(/\s+/g, "");
    if (s.length < 4) return "•".repeat(s.length);
    return `•••• ${s.slice(-4)}`;
}

export function maskEmail(e) {
    if (!e || !e.includes("@")) return e;
    const [n, d] = e.split("@");
    const head = n.slice(0, Math.min(2, n.length));
    return `${head}${"•".repeat(Math.max(1, n.length - 2))}@${d}`;
}
